import React, { useState, useRef, useEffect, useMemo } from "react";
import { raiseWidgetHost } from "@/lib/host-stacking";
import { MessageSquare, X, Sparkles, ArrowUp, Maximize2, Minimize2, HelpCircle } from "lucide-react";
// ============ FRAMER MOTION IMPORTS - START ============
import { motion, AnimatePresence } from "framer-motion";
// ============ FRAMER MOTION IMPORTS - END ============
import { useIsMobile } from "@/hooks/use-mobile";
import { VisitorInfo } from "@/shared/types/chat-widget-visitor";
import { UserInfoFormValues } from "./UserInfoForm";
import { identifyVisitor, saveContactId } from "@/utils/visitor-identification";
import { supabase } from "@/integrations/supabase/client";
import { fetchMessages } from "@/lib/widget-session";
import { getContactInfo } from "@/utils/contact-management";
import { isSilentMessage } from "@/utils/message-parser";
import { Message } from "@/lib/chat-engine";
import {
  QuickActionItem,
  QuickActionsConfig,
  TeamAvatarsConfig,
  HomeCopyConfig,
  HomeDesignConfig,
  HomeCardsConfig,
  UserInfoConfig,
} from "@/lib/quick-actions";
import { ORB_IMG } from "@/assets/orb";
import ChatSession from "./ChatSession";
import ChatHome from "./ChatHome";
import ChatSessionsList from "./ChatSessionsList";
import OrdersView from "./OrdersView";
import ManageSubscriptionsView from "./ManageSubscriptionsView";
import RewardsView from "./RewardsView";
import type { RewardsConfig } from "@/lib/rewards";
import HelpArticleView from "./HelpArticleView";
import HelpIndexView from "./HelpIndexView";
import { pathLabel, buildPageContext, withPageContext } from "../lib/page-context";
import { launcherAnchorX, liftedOffset, watchBottomBar } from "../lib/bottom-bar";
import { ASSET_BASE } from "../lib/asset-base";
import { ORB_LAUNCHER_DEFAULT_SIZE, orbLauncherAssets } from "../lib/launcher-orb";
import type { MessengerConfig } from "../lib/messenger";
import OrbCanvas from "./OrbCanvas";
import { warmGrainBackdrop } from "./FilmGrain";

type ChatView = "home" | "chat" | "sessions" | "orders" | "article" | "help" | "manage" | "rewards";

// "corner" = classic floating bubble + side panel.
// "center" = the centred "Ask" gradient pill + suggestion chips + centre modal
// (formerly the separate "Rylo" widget). BOTH modes render the exact same
// view router (renderView) so every feature is shared and can never drift.
type DisplayMode = "corner" | "center";

// The configured brand colours apply to the HOME screen only. Every screen
// after home (chat / sessions) renders in this fixed black-&-white palette so
// the brand design never bleeds into the conversation views.
const MONO_COLORS = {
  backgroundColor: "#FFFFFF",
  textColor: "#000000",
  secondaryTextColor: "#6B7280",
  borderColor: "#E5E7EB",
  inputBackgroundColor: "#F3F4F6",
  buttonBackgroundColor: "#000000",
  buttonTextColor: "#FFFFFF",
};

interface CustomColors {
  backgroundColor?: string;
  textColor?: string;
  secondaryTextColor?: string;
  borderColor?: string;
  inputBackgroundColor?: string;
  buttonBackgroundColor?: string;
  buttonTextColor?: string;
  /** Home-screen header + subheader colour. Home view only — MONO_COLORS has no
   *  entry, so the conversation views are unaffected. Undefined = legacy white. */
  headerTextColor?: string;
  /** "Powered by" footer colour (home view only). Undefined = legacy black. */
  footerTextColor?: string;
  /** Home-screen gradient colour (home view only). Undefined = the accent. */
  homeBackgroundColor?: string;
}

/** One device's WhatsApp-launcher appearance (web_chat_widgets.launcher.desktop/.mobile).
 *  Any absent field falls back to the legacy flat launcher fields, then defaults. */
interface LauncherVariant {
  size?: number; // button height px (icon scales with it); default 56
  radius?: number; // px corner radius; default 9999 (pill)
  label?: string; // "" = icon only
  corner?: "bottom-right" | "bottom-left"; // default bottom-right
  offset_x?: number; // px gap from the chosen side; default 24
  offset_y?: number; // px gap from the bottom; default 24
}

export interface ChatWidgetProps {
  organizationId: string;
  widgetId: string;
  visitorInfo: VisitorInfo;
  position?: "bottom-right" | "bottom-left" | "top-right" | "top-left";
  theme?: "light" | "dark";
  bubbleIcon?: React.ReactNode;
  headerText?: string;
  subheaderText?: string;
  agentName?: string;
  preloginHeaderText?: string;
  preloginSubheaderText?: string;
  customIcons?: {
    userWeb?: React.ReactNode;
    botWeb?: React.ReactNode;
    humanWeb?: React.ReactNode;
  };
  customColors?: CustomColors;
  previewMode?: string;
  ryloMode?: boolean;
  twoFa?: boolean;
  whatsapp?: { number: string; prefill_text?: string } | null;
  /** Facebook Page for the home screen's "Chat on Messenger" card (m.me). */
  messenger?: MessengerConfig | null;
  shopifyWebsite?: string | null;
  /** The signed-in store customer's id, read from the storefront by the loader
   *  (11 Sep 2026). Present = signed in, absent = a visitor. Used to pick which
   *  quick actions to show — never as proof of identity. */
  shopifyCustomerId?: string | null;
  /** Signed-in store customer's first name, server-rendered by the Shopify
   *  app embed. Fills a `{first_name}` token in the greeting copy. */
  shopifyCustomerFirstName?: string | null;
  storefront?: boolean;
  iconDisplay?: { userRaw?: boolean; userLarge?: boolean; botRaw?: boolean; humanRaw?: boolean };
  // Centre ("Rylo") display-mode copy, editable per widget via the
  // web_chat_widgets.center_config jsonb. Falls back to built-in defaults.
  centerConfig?: { suggestions?: string[]; placeholder?: string };
  /** Home-screen quick actions ("Search for help" card) — chat / info-article /
   *  WhatsApp / link buttons, editable per widget via web_chat_widgets.quick_actions.
   *  Null/absent = card hidden (legacy widgets unchanged). */
  quickActions?: QuickActionsConfig | null;
  /** Intercom-style team avatar cluster beside the home-screen logo
   *  (web_chat_widgets.team_avatars). Null/absent = hidden. */
  teamAvatars?: TeamAvatarsConfig | null;
  /** Editable home-card labels (web_chat_widgets.home_copy).
   *  Absent fields fall back to the built-in defaults. */
  homeCopy?: HomeCopyConfig | null;
  /** Pre-chat contact form rules + copy (web_chat_widgets.user_info):
   *  mandatory email, no-skip, localisable labels. Null/absent = the
   *  historical form (email optional, Skip shown), legacy unchanged. */
  userInfoConfig?: UserInfoConfig | null;
  /** Built-in home-card visibility (web_chat_widgets.home_cards):
   *  chat:false hides the web-chat entry from the home screen. */
  homeCards?: HomeCardsConfig | null;
  /** Home-screen design (web_chat_widgets.home_design): greeting/subheader
   *  sizes, alignment, the intro paragraph + its style. Null = legacy. */
  homeDesign?: HomeDesignConfig | null;
  /** Org slug — powers the "Open in help center" article link. */
  orgSlug?: string | null;
  /** Visitor's ISO2 country (IP geo) — regional phone-selector default. */
  visitorCountry?: string | null;
  /** Loop Subscriptions (loopwork.co) connected for this org — the My Orders
   *  view adds a read-only Subscriptions section when true. */
  loopEnabled?: boolean;
  /** FlowIQ's own subscription engine opted in for the widget — same My Orders
   *  section, served by customer-portal's /widget-subscriptions route. */
  flowiqSubsEnabled?: boolean;
  /** Per-org switch for the "Manage my subscription" home card + the Manage
   *  button in My Orders (feature_flags.widget_manage_subs). Undefined = shown,
   *  so an older config payload behaves exactly as before; only an explicit
   *  false hides them. Does NOT touch the My Orders subscriptions list. */
  manageSubsEnabled?: boolean;
  /** Manage view may verify visitors with a one-time code instead of a store
   *  login (per-org flag widget_manage_no_login; server-enforced). */
  manageNoLogin?: boolean;
  /** Cart-line subscription orgs (subscription_checkout.every_weeks): the Manage
   *  view's how-often choices in weeks; "add a product" then takes any variant. */
  subscriptionWeeks?: number[] | null;
  /** Org runs a Passes loyalty programme (loyalty_config row exists) — presence
   *  only, no secret leaves the server. Gates the rewards card WITH the
   *  per-widget rewardsConfig below. */
  loyaltyEnabled?: boolean;
  /** Per-widget rewards panel config (web_chat_widgets.rewards). Null/absent =
   *  feature off, legacy widgets unchanged; the card needs enabled:true AND
   *  loyaltyEnabled. */
  rewardsConfig?: RewardsConfig | null;
  /** Launcher config (web_chat_widgets.launcher). mode "whatsapp" replaces the
   *  whole chat with a single button that opens wa.me — no panel ever mounts.
   *  Null/absent or mode "chat" = the normal widget, so legacy is unchanged.
   *  desktop/mobile carry per-device variants (viewport <768px = mobile); the
   *  flat radius/label are the legacy fields and per-field fallback. */
  launcher?: {
    mode?: "chat" | "whatsapp";
    /** Glyph on the chat launcher: "whatsapp" draws the WhatsApp mark instead
     *  of the speech bubble (11 Sep 2026). Absent/"chat" = unchanged. */
    icon?: "chat" | "whatsapp" | "orb";
    radius?: number;
    label?: string;
    background_color?: string; // default WhatsApp green #25D366
    text_color?: string; // icon + label colour, default #FFFFFF
    /** { "<path>": "<caption>" } — a caption above the launcher on that page
     *  only (hidden_paths syntax; "$" = exact). Barkyn: "Fale com um
     *  assistente" on the plan page (17 Sep 2026). */
    path_labels?: Record<string, string> | null;
    /** Measure whatever the page pins to the bottom of the viewport and sit
     *  above it (22 Sep 2026, Barkyn's plan-page purchase bar). Off = the
     *  configured gap, exactly as before. See lib/bottom-bar. */
    avoid_bottom_bars?: boolean;
    desktop?: LauncherVariant | null;
    mobile?: LauncherVariant | null;
  } | null;
  /** Page hiding (web_chat_widgets.hidden_paths): path fragments where the
   *  widget must not render at all — e.g. ["/cart", "/checkout"] so the button
   *  never obstructs checkout controls. Case-insensitive substring match on
   *  location.pathname. Null/empty = show everywhere (legacy unchanged). */
  hiddenPaths?: string[] | null;
  // Host-controlled mode (e.g. the FlowIQ app sidebar launcher): the host
  // supplies its own launcher button and drives open/close.
  hideLauncher?: boolean; // suppress the built-in launcher (Ask bar + chips)
  defaultOpen?: boolean; // mount with the chat already open
  onClose?: () => void; // called when the user closes, so the host can tear down
  // ============ FRAMER MOTION: enableAnimations prop - START ============
  enableAnimations?: boolean; // Internal prop passed to child components
  // ============ FRAMER MOTION: enableAnimations prop - END ============
}

// ============ FRAMER MOTION CONFIG - START ============
// Set to false to disable animations on mobile devices
const ENABLE_MOBILE_ANIMATIONS = true;
// Screens. The HOME SCREEN IS THE FLOOR: it mounts when the panel opens (with
// its arrival stagger) and stays mounted, painted, underneath every other view.
// A sub-view (chat, help, orders, ...) renders in an absolutely positioned
// frame over it and is on screen in the frame after the tap; Back unmounts it
// and the home is simply there again. There is deliberately NO opacity
// animation between views: on a real GPU the first frame of a cross-fade cost
// 20-150 ms (a render surface for the fading group plus the raster it
// uncovers), which read as a hitch, and a transition that can stutter on a
// top-end Mac will stutter on a phone. An instant switch has nothing to
// stutter; what remains is the new screen's own first paint.
const getGlobalOptions = (): Partial<ChatWidgetProps> & { isRylo?: boolean } => {
  if (typeof window !== "undefined") {
    const options = window.__CHAT_WIDGET_OPTIONS__ || {};

    const scriptTag = document.querySelector("script[data-organization-id]");

    if (scriptTag) {
      const customColors = {
        backgroundColor: scriptTag.getAttribute("data-background-color"),
        textColor: scriptTag.getAttribute("data-text-color"),
        secondaryTextColor: scriptTag.getAttribute("data-secondary-text-color"),
        borderColor: scriptTag.getAttribute("data-border-color"),
        inputBackgroundColor: scriptTag.getAttribute(
          "data-input-background-color"
        ),
        buttonBackgroundColor: scriptTag.getAttribute(
          "data-button-background-color"
        ),
        buttonTextColor: scriptTag.getAttribute("data-button-text-color"),
      };

      const definedColors = Object.fromEntries(
        Object.entries(customColors).filter(([_, value]) => value !== null)
      );

      const isRylo = scriptTag.getAttribute("rylo") === "true";
      const twoFa = scriptTag.getAttribute("data-2fa") === "true";

      return {
        ...options,
        customColors:
          Object.keys(definedColors).length > 0 ? definedColors : undefined,
        previewMode:
          scriptTag.getAttribute("data-preview-mode") || options.previewMode,
        isRylo,
        twoFa,
      };
    }

    return options;
  }
  return {};
};

const ChatWidget: React.FC<ChatWidgetProps> = (props) => {
  // Script-tag attributes / window options can't change after mount — read
  // them once instead of querying the DOM on every render.
  const globalOptions = useMemo(getGlobalOptions, []);
  let {
    organizationId,
    widgetId,
    visitorInfo,
    position = globalOptions.position || "bottom-right",
    theme = globalOptions.theme || "dark",
    bubbleIcon,
    headerText,
    subheaderText,
    preloginHeaderText,
    preloginSubheaderText,
    customIcons,
    customColors = {},
    previewMode = globalOptions.previewMode,
  } = props;

  // Display mode: the old `rylo="true"` script attribute / `ryloMode` prop now
  // selects the centred "Ask" style. Everything below renders the SAME views in
  // both modes — only the launcher + outer container chrome differ.
  const displayMode: DisplayMode =
    props.ryloMode || globalOptions.isRylo ? "center" : "corner";

  customColors = { ...globalOptions.customColors, ...customColors };

  organizationId = organizationId || globalOptions.organizationId || "default";

  const [isOpen, setIsOpen] = useState(!!props.defaultOpen);
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentView, setCurrentView] = useState<ChatView>("home");
  // Cached orders survive view switches so we don't flash a loading state
  // when re-entering the Orders view. OrdersView still refetches on mount
  // and hands the fresh list back through onOrdersFetched.
  const [cachedOrders, setCachedOrders] = useState<any[] | null>(null);
  // Starter message (centre-mode chip click / "Ask" bar, or a home-screen
  // quick action in either mode). The chat opens first; ChatSession fires it
  // once its history has loaded, then calls back to clear it so re-opening
  // doesn't resend.
  const [pendingCenterMessage, setPendingCenterMessage] = useState<string | null>(
    null
  );
  // Whether the pending message auto-sends or only pre-fills the input
  // (quick actions with send_mode "prefill").
  const [pendingMode, setPendingMode] = useState<"send" | "prefill">("send");
  // Text typed into the centre-mode "Ask" bar before a chat is opened.
  const [centerInput, setCenterInput] = useState("");
  // Help-centre article currently open (currentView "article").
  const [activeArticleId, setActiveArticleId] = useState<string | null>(null);
  // Where the open article was reached from — its back button returns there.
  const articleOriginRef = useRef<"home" | "help">("home");
  // Whether the Help page should focus its search box on mount (true when
  // opened by clicking the home screen's search input, not the ?-chip).
  const helpAutoFocusRef = useRef(false);
  // Enlarged state — toggled by the PERMANENT top-right control. The whole
  // panel smoothly grows in place and STAYS enlarged across view changes and
  // open/close until the visitor shrinks it (desktop only; mobile is already
  // fullscreen).
  const [expanded, setExpanded] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  // Render the home screen's grained background ahead of the first open, for
  // the two panel heights this page can show (desktop corner panel, phone
  // fullscreen), so the home has its texture on its first frame. Mirrors the
  // sizes in cornerPanelStyle and ChatHome's colour choice.
  useEffect(() => {
    const homeColor =
      customColors.homeBackgroundColor || customColors.buttonBackgroundColor || "#2563eb";
    const panelColor = customColors.backgroundColor || "#FFFFFF";
    const warm = () => {
      const dpr = window.devicePixelRatio || 1;
      const vh = window.innerHeight;
      warmGrainBackdrop({ homeColor, panelColor, gradient: true, cssHeight: Math.min(704, vh - 104) / 0.88 - 2, scale: dpr * 0.88 });
      warmGrainBackdrop({ homeColor, panelColor, gradient: true, cssHeight: vh, scale: dpr });
    };
    warm();
    let t: ReturnType<typeof setTimeout> | undefined;
    const onResize = () => {
      clearTimeout(t);
      t = setTimeout(warm, 300);
    };
    window.addEventListener("resize", onResize);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", onResize);
    };
  }, [customColors.homeBackgroundColor, customColors.buttonBackgroundColor, customColors.backgroundColor]);
  const compactHome = displayMode === "corner" &&
    props.homeDesign?.layout === "compact" && currentView === "home";
  const mobileFullscreen = isMobile && !compactHome;
  // Current path for hiddenPaths matching. Full page loads re-mount the widget;
  // popstate covers SPA-style back/forward navigation.
  const [pathname, setPathname] = useState(() =>
    typeof window !== "undefined" ? window.location.pathname : "/",
  );
  useEffect(() => {
    const onNav = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", onNav);
    return () => window.removeEventListener("popstate", onNav);
  }, []);
  // Page-aware launcher (17 Sep 2026): a caption above the launcher on chosen
  // pages, and a context line the page already knows (Barkyn's plan answers)
  // appended to every WhatsApp opener there — see lib/page-context.
  const launcherCaption = pathLabel(props.launcher?.path_labels, pathname);
  const pageContext = useMemo(() => {
    let storage: { getItem(k: string): string | null } | null = null;
    try {
      storage = typeof window !== "undefined" ? window.localStorage : null;
    } catch {
      storage = null;
    }
    const search = typeof window !== "undefined" ? window.location.search : "";
    return buildPageContext(props.quickActions?.context, pathname, search, storage);
  }, [props.quickActions?.context, pathname]);
  const captionStyle: React.CSSProperties = {
    position: "fixed",
    zIndex: 2147483647,
    padding: "8px 12px",
    borderRadius: 9999,
    border: "none",
    background: "#FFFFFF",
    color: "#111111",
    fontFamily: "system-ui, sans-serif",
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1,
    boxShadow: "0 4px 14px rgba(0,0,0,.18)",
    whiteSpace: "nowrap",
    cursor: "pointer",
  };

  // ============ FRAMER MOTION: Determine if animations enabled - START ============
  const enableAnimations = ENABLE_MOBILE_ANIMATIONS || !isMobile;
  // ============ FRAMER MOTION: Determine if animations enabled - END ============

  const [userInfo, setUserInfo] = useState<UserInfoFormValues | null>(null);
  const [localVisitorInfo, setLocalVisitorInfo] = useState<VisitorInfo | null>(
    null
  );
  const [contactId, setContactId] = useState<string | null>(null);
  const [recentMessage, setRecentMessage] = useState<{
    message: string;
    senderName: string;
    createdAt: string;
    senderType: string;
  } | null>(null);

  const effectiveVisitorInfo = visitorInfo || localVisitorInfo;

  // Centre mode keeps the old "Rylo" default name when none is configured.
  const effectiveHeaderText =
    displayMode === "center" ? headerText || "Rylo" : headerText;

  // How far the launcher sits in from its corner, per device
  // (web_chat_widgets.launcher.{desktop,mobile}.offset_{x,y}). 24px when unset,
  // which is exactly what the old bottom-6/right-6 classes rendered — so a
  // widget that has never set a gap is pixel-identical. The corner itself still
  // comes from `position`; only the distance is configurable.
  const launcherGap =
    (isMobile ? props.launcher?.mobile : props.launcher?.desktop) ?? {};
  const launcherGapX =
    typeof launcherGap.offset_x === "number" ? launcherGap.offset_x : 24;
  const launcherGapY =
    typeof launcherGap.offset_y === "number" ? launcherGap.offset_y : 24;

  // Animated orb launcher (launcher.icon "orb"): per-device size like the
  // WhatsApp button, 72 when unset. Only the CLOSED bubble is the orb — open,
  // it is the plain X so the close target stays obvious.
  const orbLauncher = props.launcher?.icon === "orb";
  const orbSize =
    typeof launcherGap.size === "number" && launcherGap.size > 0
      ? launcherGap.size
      : ORB_LAUNCHER_DEFAULT_SIZE;
  const orbAssets = orbLauncherAssets(ASSET_BASE);

  // The page's OWN sticky bottom bar (launcher.avoid_bottom_bars, 22 Sep 2026).
  // Barkyn's plan page pins a purchase bar to the bottom and the launcher sat
  // inside it, over the price. When the flag is on we measure that bar live and
  // sit above it; off (every other widget) nothing here changes a pixel.
  const avoidBottomBars = props.launcher?.avoid_bottom_bars === true;
  const whatsappLauncher = props.launcher?.mode === "whatsapp";
  const launcherSize = whatsappLauncher
    ? typeof launcherGap.size === "number" && launcherGap.size > 0
      ? launcherGap.size
      : 56
    : orbLauncher
      ? orbSize
      : 56;
  const launcherCorner: "left" | "right" = whatsappLauncher
    ? launcherGap.corner === "bottom-left"
      ? "left"
      : "right"
    : position.includes("left")
      ? "left"
      : "right";
  const [bottomBarHeight, setBottomBarHeight] = useState(0);
  useEffect(() => {
    if (!avoidBottomBars || typeof window === "undefined") return;
    // A top-corner chat launcher is never in a bottom bar's way.
    if (!whatsappLauncher && position.includes("top")) return;
    return watchBottomBar(
      document,
      window,
      () => launcherAnchorX(window.innerWidth, launcherCorner, launcherGapX, launcherSize),
      setBottomBarHeight,
    );
  }, [
    avoidBottomBars,
    whatsappLauncher,
    position,
    launcherCorner,
    launcherGapX,
    launcherSize,
    pathname,
  ]);
  /** The gap the launcher actually renders with: its own, or clear of the bar. */
  const launcherBottom = liftedOffset(
    launcherGapY,
    avoidBottomBars ? bottomBarHeight : 0,
  );

  const getButtonPositionStyle = (): React.CSSProperties => ({
    ...(position.includes("top")
      ? { top: launcherGapY }
      : { bottom: launcherBottom }),
    ...(position.includes("left")
      ? { left: launcherGapX }
      : { right: launcherGapX }),
  });

  const getWidgetPositionClasses = () => {
    if (compactHome) return "fixed z-[2147483646] rounded-2xl";
    if (isMobile) return "fixed inset-0 z-[2147483647] rounded-none";

    switch (position) {
      case "bottom-left":
        return "fixed bottom-24 left-6 w-[440px] h-[680px] z-[2147483646] rounded-2xl";
      case "top-right":
        return "fixed top-24 right-6 w-[440px] h-[680px] z-[2147483646] rounded-2xl";
      case "top-left":
        return "fixed top-24 left-6 w-[440px] h-[680px] z-[2147483646] rounded-2xl";
      case "bottom-right":
      default:
        return "fixed bottom-24 right-6 w-[440px] h-[680px] z-[2147483646] rounded-2xl";
    }
  };

  const getThemeClasses = () => {
    const defaultClasses =
      theme === "light"
        ? {
            background: "bg-white",
            text: "text-black",
            secondaryText: "text-gray-600",
            border: "border-gray-200",
            inputBg: "bg-gray-100",
            buttonBg: "bg-black",
            buttonText: "text-white",
          }
        : {
            background: "bg-black",
            text: "text-white",
            secondaryText: "text-zinc-400",
            border: "border-zinc-800",
            inputBg: "bg-zinc-900",
            buttonBg: "bg-white",
            buttonText: "text-black",
          };

    return defaultClasses;
  };

  const themeClasses = getThemeClasses();

  // Home screen uses the org's brand colours; all later screens use MONO_COLORS.
  const viewColors = currentView === "home" ? customColors : MONO_COLORS;

  const customStylesObj: React.CSSProperties = {};
  if (customColors.backgroundColor)
    customStylesObj.backgroundColor = customColors.backgroundColor;
  if (customColors.textColor) customStylesObj.color = customColors.textColor;
  if (customColors.borderColor)
    customStylesObj.borderColor = customColors.borderColor;

  // Enlarged state: the panel SMOOTHLY GROWS IN PLACE from its corner anchor —
  // same element, same anchor, only width/height animate (no repositioning, so
  // it reads as the existing widget getting bigger). zoom stays constant so the
  // content never rescales mid-animation; every dimension divides by it to land
  // at the intended RENDERED size. Mobile is already fullscreen.
  const EXPAND_EASE = "cubic-bezier(0.16, 1, 0.3, 1)";
  const cornerExpanded = expanded && !isMobile;
  // Sizes MATCH INTERCOM's messenger exactly (measured against it on
  // barkyn.com, 17 Aug 2026): compact renders 400px × min(704px, vh−104px);
  // expanded renders min(800px, vw−40px) × (vh−108px). Everything stays
  // viewport-clamped so browser zoom shrinks the panel fluidly, and our
  // class-based corner offsets already render at Intercom's ~84px bottom /
  // ~20px side anchors under the 0.88 zoom.
  const cornerPanelStyle: React.CSSProperties = {
    width: isMobile
      ? undefined
      : cornerExpanded
        ? "calc(min(800px, 100vw - 40px) / 0.88)"
        : "calc(min(400px, 100vw - 40px) / 0.88)",
    // Mobile is FULL-BLEED to the screen edge (the Intercom look): 100dvh
    // tracks the browser bars exactly, and inset-0 sizes it identically on
    // browsers without dvh — no border, no gap above the bottom bar.
    height: isMobile
      ? "100dvh"
      : cornerExpanded
        ? "calc((100vh - 108px) / 0.88)"
        : "calc(min(704px, 100vh - 104px) / 0.88)",
    ...(isMobile
      ? {}
      : {
          zoom: 0.88,
        }),
    // A launcher SIZE above the classic 56px outgrows the `bottom-24` /
    // `top-24` classes (the panel's edge sits ~85px in, a 56px bubble's top at
    // 80). Only then lift the panel clear of the disc: gap + size + 8px,
    // divided by the zoom so it lands there in real pixels. At the default 56
    // the classes stand, so the orb sits exactly like the old bubble.
    ...(!isMobile && orbLauncher && orbSize > 56
      ? position.includes("top")
        ? { top: (launcherGapY + orbSize + 8) / 0.88 }
        : { bottom: (launcherGapY + orbSize + 8) / 0.88 }
      : {}),
    // No background-colour transition: every view paints its own opaque surface,
    // so the panel colour only shows through the compact welcome card, and a
    // colour tween here repainted the whole bordered, shadowed panel layer on
    // every frame of a view switch.
    transition: `width 400ms ${EXPAND_EASE}, height 400ms ${EXPAND_EASE}`,
    backgroundColor: "#FFFFFF",
    ...(currentView === "home" ? customStylesObj : {}),
    // A small welcome card has no minimum height or desktop zoom. Keep it
    // above the launcher (and any sticky purchase bar), even on phones.
    ...(compactHome ? {
      width: `min(340px, calc(100vw - ${2 * Math.max(12, Math.min(launcherGapX, 24))}px))`,
      height: "auto",
      maxHeight: `calc(100dvh - ${(position.includes("top") ? launcherGapY : launcherBottom) + launcherSize + 24}px)`,
      zoom: 1,
      top: position.includes("top") ? launcherGapY + launcherSize + 12 : "auto",
      bottom: position.includes("top") ? "auto" : launcherBottom + launcherSize + 12,
      left: position.includes("left") ? Math.max(12, Math.min(launcherGapX, 24)) : "auto",
      right: position.includes("left") ? "auto" : Math.max(12, Math.min(launcherGapX, 24)),
    } : {}),
  };


  // Whatever opened the panel — the launcher, a deep link, a quick action or
  // defaultOpen — make sure nothing on the merchant's page can paint over it.
  // One effect rather than five call sites, so a future open path cannot
  // forget it. See lib/host-stacking for why document order is what matters.
  useEffect(() => {
    if (isOpen) raiseWidgetHost();
  }, [isOpen]);

  const toggleChat = () => {
    // Closing → notify the host (e.g. the FlowIQ sidebar launcher) so it can tear down.
    if (isOpen) props.onClose?.();
    setIsOpen(!isOpen);

    if (isMobile && props.homeDesign?.layout !== "compact") {
      // (Scroll locking is handled by the body position:fixed effect below —
      // overflow:hidden alone is ignored by iOS and caused tap-target drift.)

      if (!isOpen) {
        const metaViewport = document.querySelector("meta[name=viewport]");
        if (!metaViewport) {
          const meta = document.createElement("meta");
          meta.name = "viewport";
          meta.content =
            "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover";
          document.head.appendChild(meta);
        } else {
          metaViewport.setAttribute(
            "content",
            "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover"
          );
        }
      } else {
        const metaViewport = document.querySelector("meta[name=viewport]");
        if (metaViewport) {
          metaViewport.setAttribute(
            "content",
            "width=device-width, initial-scale=1.0"
          );
        }
      }
    }
  };

  useEffect(() => {
    if (!visitorInfo && !localVisitorInfo && organizationId) {
      try {
        const generatedVisitorInfo = identifyVisitor(organizationId);
        setLocalVisitorInfo(generatedVisitorInfo);
        console.log("Generated visitor info:", generatedVisitorInfo);
      } catch (error) {
        console.error("Error generating visitor info:", error);
      }
    }
  }, [visitorInfo, localVisitorInfo, organizationId]);

  // Function to refresh user info from localStorage
  const refreshUserInfo = () => {
    const { userInfo: savedUserInfo } = getContactInfo(organizationId);
    if (savedUserInfo) {
      setUserInfo({
        fullName: savedUserInfo.fullName || "",
        email: savedUserInfo.email || "",
        phone: savedUserInfo.phone || "",
        countryCode: "+1",
        phoneNumber: savedUserInfo.phone || "",
      });
    }

    const contactStorageKey = `flowapt_contact_${organizationId}`;
    const savedContactId = localStorage.getItem(contactStorageKey);
    if (savedContactId) {
      setContactId(savedContactId);
    }
  };

  useEffect(() => {
    refreshUserInfo();
  }, [organizationId]);

  // Handle preview mode - auto-open widget when previewMode is "yes"
  useEffect(() => {
    if (previewMode === "yes") {
      setIsOpen(true);
    }
  }, [previewMode]);

  // Open chat view (all messages loaded automatically by ChatEngine based on contactId)
  const openChat = () => {
    setCurrentView("chat");
  };

  const handleSaveContactId = (newContactId: string) => {
    if (newContactId && newContactId !== contactId) {
      saveContactId(organizationId, newContactId);
      setContactId(newContactId);
      // Re-read userInfo from localStorage so the orders background
      // fetch (which gates on userInfo.phone) can trigger immediately
      // after the user fills in the contact form.
      refreshUserInfo();
    }
  };

  useEffect(() => {
    const contactStorageKey = `flowapt_contact_${organizationId}`;
    const savedContactId = localStorage.getItem(contactStorageKey);

    if (savedContactId) {
      setContactId(savedContactId);
    }

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === contactStorageKey) {
        setContactId(e.newValue);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [organizationId]);

  const senderNameFor = (senderType: string) =>
    senderType === 'human-web'
      ? 'Support'
      : senderType === 'user-web'
        ? 'You'
        : props.agentName || 'AI Agent';

  // Fetch recent message function (defined outside useEffect so it can be called from other places)
  const fetchRecentMessage = async () => {
    if (!contactId || !organizationId) {
      setRecentMessage(null);
      return;
    }

    try {
      // Pull a few recent rows THROUGH THE TOKEN API and pick the latest
      // NON-silent one. Silent replies ({"silent":true,...}) are
      // internal-only and must not surface in the home-screen preview.
      const rows = await fetchMessages(organizationId, contactId, {
        order: "desc",
        limit: 10,
      });
      if (rows === null) return;

      const recent = rows.find((m) => !isSilentMessage(m.message));

      if (recent) {
        setRecentMessage({
          message: recent.message,
          senderName: senderNameFor(recent.sender_type),
          createdAt: recent.created_at,
          senderType: recent.sender_type,
        });
      } else {
        setRecentMessage(null);
      }
    } catch (error) {
      console.error('Error fetching recent message:', error);
    }
  };

  // Keep the home-screen "Recent message" preview fresh: one fetch when the
  // view changes away from chat, plus a light 10s poll while the panel is
  // open and the tab visible (replaces the old anon realtime channel — no
  // anon consumer of helpdesk_messages may remain).
  useEffect(() => {
    if (currentView === "chat") return;

    fetchRecentMessage();

    if (!contactId || !organizationId || !isOpen) return;

    const t = setInterval(() => {
      if (!document.hidden) fetchRecentMessage();
    }, 10_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId, organizationId, currentView, isOpen]);

  const handleNavigateHome = () => {
    setCurrentView("home");
    // Refresh identity when navigating back to home. The recent-message
    // preview refetches via the effect above (it re-runs on view change).
    refreshUserInfo();
  };

  // Help is available whenever the widget has quick-actions config (the same
  // gate as the home "Search for help" card) — legacy widgets without it get
  // no ?-chip and no Help page, unchanged.
  const helpAvailable =
    !!props.quickActions && props.quickActions.enabled !== false;

  // Open the Help page. autoFocus = the home search-input path (the visitor
  // clicked somewhere to TYPE, so the Help page's search box takes the caret).
  const openHelp = (autoFocus: boolean) => {
    helpAutoFocusRef.current = autoFocus;
    setCurrentView("help");
  };

  // Open the subscription manager. Auto-ENLARGES on desktop (mobile is already
  // fullscreen) — the visitor can shrink it back with the permanent top-right
  // control; management reads far better wide.
  const openManage = () => {
    setCurrentView("manage");
    if (!isMobile) setExpanded(true);
  };
  const manageAvailable =
    (props.flowiqSubsEnabled === true || props.loopEnabled === true) &&
    props.manageSubsEnabled !== false;

  // Rewards (Passes loyalty) — needs the org programme AND the per-widget
  // opt-in, so a merchant can run loyalty without surfacing it in this widget.
  const rewardsAvailable =
    props.loyaltyEnabled === true && props.rewardsConfig?.enabled === true;
  const openRewards = () => {
    setCurrentView("rewards");
  };

  // Open an info page. Clicking a question topic ENLARGES the widget (desktop
  // only — mobile is already fullscreen); articles read far better wide, and
  // the enlarged state then sticks per the normal expanded rules.
  const openArticle = (articleId: string, origin: "home" | "help") => {
    articleOriginRef.current = origin;
    setActiveArticleId(articleId);
    setCurrentView("article");
    if (!isMobile) setExpanded(true);
  };

  // Home-screen quick action ("Search for help" card / topic buttons):
  // chat  → open the chat and fire (or pre-fill) the configured message
  // article → open the in-widget info page
  // whatsapp → wa.me with the message pre-filled (item number, else the
  //            widget's WhatsApp fallback number)
  // link  → open the URL in a new tab
  // email → compose to the configured address (mailto)
  // help  → open the Help page (same place the ?-chip goes)
  // popup → open the merchant's own FlowIQ popup on the page, falling back to
  //         the chat when it is not there or has nothing to show
  const openInChat = (msg: string, item: QuickActionItem) => {
    setPendingMode(item.send_mode === "prefill" ? "prefill" : "send");
    setPendingCenterMessage(msg);
    setCurrentView("chat");
  };

  const handleQuickAction = (item: QuickActionItem) => {
    const msg = (item.message || "").trim() || item.label;
    switch (item.action) {
      case "chat":
        openInChat(msg, item);
        break;
      case "popup": {
        // The popup script runs on the SAME page (both are plain embeds on the
        // storefront), so its global is reachable directly — no postMessage.
        const api = (
          window as unknown as {
            ReactPopupWidget?: { open?: () => Promise<boolean> };
          }
        ).ReactPopupWidget;
        if (typeof api?.open !== "function") {
          // Popup not installed, or an older bundle without open(): the
          // visitor must still get somewhere useful.
          openInChat(msg, item);
          return;
        }
        // Close the panel so the popup is not covered by it.
        setIsOpen(false);
        api
          .open()
          .then((shown) => {
            if (!shown) {
              setIsOpen(true);
              openInChat(msg, item);
            }
          })
          .catch(() => {
            setIsOpen(true);
            openInChat(msg, item);
          });
        break;
      }
      case "article":
        if (!item.article_id) return;
        openArticle(item.article_id, "home");
        break;
      case "whatsapp": {
        const number = (item.number || props.whatsapp?.number || "").replace(
          /[^\d]/g,
          ""
        );
        if (!number) return;
        const opener = withPageContext(msg, pageContext);
        const text = opener ? `?text=${encodeURIComponent(opener)}` : "";
        window.open(`https://wa.me/${number}${text}`, "_blank");
        break;
      }
      case "link":
        if (item.url) window.open(item.url, "_blank", "noopener");
        break;
      case "email": {
        const email = (item.email || "").trim();
        if (!email) return;
        const subject = (item.subject || "").trim();
        // mailto via location.href (not window.open) — popup blockers eat
        // opened mailto windows on several browsers; href hands it straight
        // to the mail client without navigating the page.
        window.location.href = `mailto:${email}${
          subject ? `?subject=${encodeURIComponent(subject)}` : ""
        }`;
        break;
      }
      case "help":
        openHelp(false);
        break;
    }
  };

  const handleNavigateToSessions = () => {
    setCurrentView("sessions");
  };

  const handleNavigateToOrders = () => {
    setCurrentView("orders");
  };

  // Background-fetch orders when the chat view opens so we know whether to
  // show the "My Orders" icon in the header. Only fires once (when
  // cachedOrders is still null) and only if the user has a phone number.
  useEffect(() => {
    if (currentView !== "chat" || cachedOrders !== null || !userInfo?.phone) return;
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("customer-orders", {
          body: { organizationId, phoneNumber: userInfo.phone },
        });
        if (!error && !data?.error && Array.isArray(data?.orders)) {
          setCachedOrders(data.orders);
        } else {
          setCachedOrders([]); // mark as fetched — no orders or error
        }
      } catch {
        setCachedOrders([]); // don't retry on failure
      }
    })();
  }, [currentView, cachedOrders, userInfo?.phone, organizationId]);

  useEffect(() => {
    if (!isOpen) {
      setCurrentView("home");
      // `expanded` deliberately survives close/reopen — the visitor chose a
      // size and it sticks until they shrink it.
      setActiveArticleId(null);
      articleOriginRef.current = "home";
      helpAutoFocusRef.current = false;
    }
  }, [isOpen]);

  // ── Mobile back-gesture navigation (Intercom parity) ──────────────────────
  // The phone's back gesture/button navigates the WIDGET instead of leaving
  // the page: subview → home → closed. Model: at most TWO history entries of
  // ours (one for "open", one for "in a subview"); UI-driven navigation
  // consumes its own entries so history never pollutes.
  const pushedRef = useRef(0);
  const ignorePopRef = useRef(0);
  const popHandlersRef = useRef({ goHome: () => {}, close: () => {} });
  popHandlersRef.current = {
    goHome: () => {
      setCurrentView("home");
      refreshUserInfo();
    },
    close: () => {
      props.onClose?.();
      setIsOpen(false);
    },
  };

  useEffect(() => {
    if (!isMobile) return;
    const onPop = () => {
      if (ignorePopRef.current > 0) {
        ignorePopRef.current--;
        return;
      }
      if (pushedRef.current === 2) {
        pushedRef.current = 1;
        popHandlersRef.current.goHome();
      } else if (pushedRef.current === 1) {
        pushedRef.current = 0;
        popHandlersRef.current.close();
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [isMobile]);

  // Open/close ↔ the first history entry.
  useEffect(() => {
    if (!isMobile) return;
    if (isOpen && pushedRef.current === 0) {
      history.pushState({ fiqWidget: 1 }, "");
      pushedRef.current = 1;
    } else if (!isOpen && pushedRef.current > 0) {
      // Closed via the UI — consume our entries silently.
      ignorePopRef.current += pushedRef.current;
      history.go(-pushedRef.current);
      pushedRef.current = 0;
    }
  }, [isOpen, isMobile]);

  // Tell the FlowIQ sign-up popup on the same page that a conversation is
  // open: its automatic trigger waits for this marker to go before it shows
  // (flowapt-popup-service src/lib/chat-presence.ts), so it never covers the
  // chat mid-conversation. Our "Get 10% off" card still opens it at once.
  useEffect(() => {
    const root = document.documentElement;
    if (isOpen) root.setAttribute("data-flowiq-chat-open", "");
    else root.removeAttribute("data-flowiq-chat-open");
    return () => root.removeAttribute("data-flowiq-chat-open");
  }, [isOpen]);

  // Subview ↔ the second history entry.
  useEffect(() => {
    if (!isMobile || !isOpen) return;
    if (currentView !== "home" && pushedRef.current === 1) {
      history.pushState({ fiqWidget: 2 }, "");
      pushedRef.current = 2;
    } else if (currentView === "home" && pushedRef.current === 2) {
      // Went home via the UI back button — consume the subview entry.
      ignorePopRef.current++;
      history.back();
      pushedRef.current = 1;
    }
  }, [currentView, isOpen, isMobile]);

  // THE mobile scroll lock, done the way iOS actually honours. body
  // overflow:hidden is half-ignored by iOS Safari: the page can still pan
  // behind the open widget (opening while scrolled, rubber-band, keyboard),
  // and once it pans, Safari's HIT-TESTING for fixed elements drifts out of
  // sync with where they paint — the top back/close buttons stop responding
  // until a resize forces re-layout. position:fixed on the body freezes the
  // page for real; the scroll offset is preserved and restored on close.
  const scrollLockYRef = useRef(0);
  useEffect(() => {
    if (!mobileFullscreen || !isOpen) return;
    const body = document.body;
    scrollLockYRef.current = window.scrollY;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    body.style.position = "fixed";
    body.style.top = `-${scrollLockYRef.current}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overflow = "hidden";
    return () => {
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.left = prev.left;
      body.style.right = prev.right;
      body.style.width = prev.width;
      body.style.overflow = prev.overflow;
      window.scrollTo(0, scrollLockYRef.current);
    };
  }, [mobileFullscreen, isOpen]);

  useEffect(() => {
    if (!mobileFullscreen || !isOpen) return;

    // Visual-viewport mirroring: iOS pans the visible area when the keyboard
    // opens for ANY input in the widget (chat box, help search) — and,
    // crucially, sometimes LEAVES that pan stranded after the keyboard closes
    // (a known Safari misbehaviour when the page can't scroll, which our
    // body lock guarantees). So there is NO on/off "keyboard mode": whenever
    // the visible area deviates from the layout viewport — keyboard open,
    // mid-animation, or a stranded pan — the panel pins itself to exactly the
    // visible region (top = offsetTop, height = vv.height), and it only
    // releases to the 100dvh style once the screen is genuinely back to
    // normal. A stranded pan therefore still shows a perfectly framed widget
    // instead of a stuck, shifted screen.
    const sync = () => {
      const el = chatContainerRef.current;
      if (!el) return;
      const vv = window.visualViewport;
      if (!vv) return;
      const layoutH = document.documentElement.clientHeight;
      const deviates = vv.offsetTop > 2 || layoutH - vv.height > 2;
      if (deviates) {
        el.style.top = `${vv.offsetTop}px`;
        el.style.height = `${vv.height}px`;
        el.style.bottom = "auto";
      } else {
        el.style.top = "";
        el.style.height = "";
        el.style.bottom = "";
      }
    };

    // Belt + braces: keyboard close animations can settle without a final
    // viewport event in some iOS versions — re-sync shortly after any input
    // inside the widget loses focus.
    let focusOutTimer: ReturnType<typeof setTimeout> | undefined;
    const onFocusOut = () => {
      clearTimeout(focusOutTimer);
      focusOutTimer = setTimeout(sync, 350);
    };

    window.addEventListener("resize", sync);
    window.addEventListener("focusout", onFocusOut);
    window.visualViewport?.addEventListener("resize", sync);
    window.visualViewport?.addEventListener("scroll", sync);
    sync();

    return () => {
      clearTimeout(focusOutTimer);
      window.removeEventListener("resize", sync);
      window.removeEventListener("focusout", onFocusOut);
      window.visualViewport?.removeEventListener("resize", sync);
      window.visualViewport?.removeEventListener("scroll", sync);
    };
  }, [mobileFullscreen, isOpen]);

  useEffect(() => {
    return () => {
      document.body.style.overflow = "";
      const metaViewport = document.querySelector("meta[name=viewport]");
      if (metaViewport) {
        metaViewport.setAttribute(
          "content",
          "width=device-width, initial-scale=1.0"
        );
      }
    };
  }, []);

  // ============ FRAMER MOTION: Toggle Button Component - START ============
  const ToggleButton = enableAnimations ? motion.button : 'button';
  const toggleButtonProps = enableAnimations ? {
    whileHover: { scale: 1.05 },
    whileTap: { scale: 0.95 },
    animate: { rotate: isOpen ? 90 : 0 },
    transition: { type: "spring" as const, stiffness: 400, damping: 25 }
  } : {};
  // ============ FRAMER MOTION: Toggle Button Component - END ============

  // ============ FRAMER MOTION: Container Component - START ============
  const Container = enableAnimations ? motion.div : 'div';
  const containerProps = enableAnimations ? {
    initial: { opacity: 0, scale: 0.96, y: isMobile ? 15 : 15 },
    animate: { opacity: 1, scale: 1, y: 0 },
    exit: { opacity: 0, scale: 0.96, y: isMobile ? 15 : 15 },
    transition: { type: "spring" as const, stiffness: 500, damping: 35, mass: 0.8 }
  } : {};
  // ============ FRAMER MOTION: Container Component - END ============

  // Permanent top-right control, rendered ABOVE every view. Desktop: ONLY the
  // make-larger/make-smaller toggle — closing happens via the launcher bubble,
  // exactly like Intercom (no X in the panel). Mobile: ONLY a close X (the
  // panel is fullscreen, so the launcher isn't reachable). White floating
  // chips (the ChatSession header-button style) so they read on any background.
  const panelControls = compactHome ? null : (
    <div className="absolute top-3 right-3 z-30 flex items-center gap-2">
      {/* ?-chip: opens the Help page. Home only — every other view either IS
          help or has its own header, and the chrome must stay uncrowded. */}
      {currentView === "home" && helpAvailable && (
        <button
          onClick={() => openHelp(false)}
          aria-label="Help"
          title="Help"
          className="h-9 w-9 rounded-full bg-white shadow-md ring-1 ring-black/5 flex items-center justify-center hover:bg-gray-50 transition-colors text-gray-700"
        >
          <HelpCircle size={17} />
        </button>
      )}
      {!isMobile && (
        <button
          onClick={() => setExpanded((e) => !e)}
          aria-label={expanded ? "Make smaller" : "Make larger"}
          title={expanded ? "Make smaller" : "Make larger"}
          className="h-9 w-9 rounded-full bg-white shadow-md ring-1 ring-black/5 flex items-center justify-center hover:bg-gray-50 transition-colors text-gray-700"
        >
          {expanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
        </button>
      )}
      {isMobile && (
        <button
          onClick={toggleChat}
          aria-label="Close chat"
          title="Close"
          className="h-9 w-9 rounded-full bg-white shadow-md ring-1 ring-black/5 flex items-center justify-center hover:bg-gray-50 transition-colors text-gray-700"
        >
          <X size={17} />
        </button>
      )}
    </div>
  );

  // ───────────────────────────────────────────────────────────────────────────
  // Shared view router — the SINGLE source of truth for what's inside the widget
  // in BOTH display modes. Add features here (or in these child components) and
  // both corner and centre styles get them automatically.
  // ───────────────────────────────────────────────────────────────────────────
  const viewKey =
      currentView === "article"
        ? activeArticleId
          ? `article-${activeArticleId}`
          : "chat"
        : currentView;
    const homeEl = (
        <ChatHome
          key="home"
          compact={compactHome}
          onClose={compactHome ? toggleChat : undefined}
          onNewChat={openChat}
          onPreviousChats={openChat}
          onViewOrders={handleNavigateToOrders}
          theme={theme}
          customColors={viewColors}
          customIcons={customIcons}
          headerText={effectiveHeaderText}
          subheaderText={subheaderText}
          preloginHeaderText={preloginHeaderText}
          preloginSubheaderText={preloginSubheaderText}
          contactId={contactId}
          organizationId={organizationId}
          userInfo={userInfo}
          recentMessage={recentMessage}
          enableAnimations={enableAnimations}
          whatsapp={props.whatsapp}
          messenger={props.messenger}
          iconDisplay={props.iconDisplay}
          quickActions={props.quickActions}
          shopifyCustomerId={props.shopifyCustomerId}
          shopifyCustomerFirstName={props.shopifyCustomerFirstName}
          onQuickAction={handleQuickAction}
          onOpenHelp={helpAvailable ? () => openHelp(true) : undefined}
          onOpenManage={manageAvailable ? openManage : undefined}
          onOpenRewards={rewardsAvailable ? openRewards : undefined}
          rewardsConfig={props.rewardsConfig}
          teamAvatars={props.teamAvatars}
          homeCopy={props.homeCopy}
          homeCards={props.homeCards}
          homeDesign={props.homeDesign}
          agentName={props.agentName}
        />
    );
    const view =
      currentView === "home" ? homeEl : currentView === "help" ? (
        <HelpIndexView
          key="help"
          organizationId={organizationId}
          onBack={handleNavigateHome}
          onOpenArticle={(id) => openArticle(id, "help")}
          autoFocusSearch={helpAutoFocusRef.current}
          searchPlaceholder={props.quickActions?.title}
          homeCopy={props.homeCopy}
          enableAnimations={enableAnimations}
        />
      ) : currentView === "article" && activeArticleId ? (
        <HelpArticleView
          key={`article-${activeArticleId}`}
          articleId={activeArticleId}
          organizationId={organizationId}
          onBack={() => {
            if (articleOriginRef.current === "help") {
              helpAutoFocusRef.current = false;
              setCurrentView("help");
            } else {
              handleNavigateHome();
            }
          }}
          expanded={expanded}
          onOpenArticle={(id) => setActiveArticleId(id)}
          relatedTitle={props.homeCopy?.related_title}
          feedbackTitle={props.homeCopy?.feedback_title}
          helpCenterUrl={
            props.orgSlug
              ? `https://help.flowapt.com/w/help/${props.orgSlug}/${activeArticleId}`
              : undefined
          }
          helpCenterLabel={props.homeCopy?.help_center_label}
          enableAnimations={enableAnimations}
        />
      ) : currentView === "sessions" ? (
        <ChatSessionsList
          key="sessions"
          organizationId={organizationId}
          contactId={contactId}
          onBack={handleNavigateHome}
          onOpenChat={openChat}
          theme={theme}
          customColors={viewColors}
        />
      ) : currentView === "manage" ? (
        <ManageSubscriptionsView
          key="manage"
          organizationId={organizationId}
          flowiqEnabled={props.flowiqSubsEnabled === true}
          loopEnabled={props.loopEnabled === true}
          manageNoLogin={props.manageNoLogin === true}
          subscriptionWeeks={props.subscriptionWeeks ?? null}
          userInfo={userInfo}
          onBack={handleNavigateHome}
          onOpenChat={() => openChat()}
        />
      ) : currentView === "rewards" ? (
        <RewardsView
          key="rewards"
          organizationId={organizationId}
          contactId={contactId}
          config={props.rewardsConfig}
          onBack={handleNavigateHome}
          onOpenChat={() => openChat()}
          storefrontUrl={props.shopifyWebsite}
        />
      ) : currentView === "orders" ? (
        <OrdersView
          key="orders"
          organizationId={organizationId}
          userInfo={userInfo}
          onBack={handleNavigateHome}
          theme={theme}
          cachedOrders={cachedOrders}
          onOrdersFetched={setCachedOrders}
          loopEnabled={props.loopEnabled === true}
          flowiqSubsEnabled={props.flowiqSubsEnabled === true}
          onManage={manageAvailable ? openManage : undefined}
        />
      ) : (
        <ChatSession
          key="chat"
          organizationId={organizationId}
          widgetId={widgetId}
          visitorInfo={effectiveVisitorInfo}
          contactId={contactId}
          userInfo={userInfo}
          messages={messages}
          setMessages={setMessages}
          onBack={handleNavigateHome}
          onSaveContactId={handleSaveContactId}
          theme={theme}
          customIcons={customIcons}
          customColors={viewColors}
          enableAnimations={enableAnimations}
          agentName={props.agentName}
          twoFa={props.twoFa}
          userInfoConfig={props.userInfoConfig}
          visitorCountry={props.visitorCountry}
          shopifyWebsite={props.shopifyWebsite}
          storefront={props.storefront}
          hasOrders={!!cachedOrders && cachedOrders.length > 0}
          onViewOrders={handleNavigateToOrders}
          pendingMessage={pendingCenterMessage ?? undefined}
          pendingMode={pendingMode}
          onPendingSent={() => setPendingCenterMessage(null)}
        />
      );

  const onHome = currentView === "home";
  const renderView = () => {
    if (!enableAnimations) return view;
    return (
      <>
        <div
          className={compactHome ? "relative min-h-0 flex flex-col" : "relative h-full flex flex-col"}
          // Covered by an opaque sub-view: out of the tab order and the
          // accessibility tree, but still mounted and painted, so Back is a
          // reveal, not a remount.
          {...(onHome ? {} : ({ inert: "" } as Record<string, string>))}
          aria-hidden={onHome ? undefined : true}
        >
          {homeEl}
        </div>
        {!onHome && (
          <div key={viewKey} className="absolute inset-0 flex flex-col">
            {view}
          </div>
        )}
      </>
    );
  };

  // ════════════════════════════════════════════════════════════════════════════
  // WHATSAPP-ONLY LAUNCHER
  // No chat, no panel — one button straight to wa.me. Placed ahead of both the
  // centre and corner chrome so neither renders. Every hook above has already
  // run, so this early return keeps hook order stable.
  // ════════════════════════════════════════════════════════════════════════════
  // ════════════════════════════════════════════════════════════════════════════
  // HIDDEN PAGES
  // On a configured path (e.g. /cart) the widget renders NOTHING — chat and
  // WhatsApp launcher alike — so it can never obstruct checkout controls.
  // Host-controlled mode (FlowIQ sidebar) is exempt: the host decides.
  // ════════════════════════════════════════════════════════════════════════════
  if (!props.hideLauncher && Array.isArray(props.hiddenPaths)) {
    // A trailing "$" on an entry means EXACT pathname, not "contains" — see the
    // loader's copy of this rule. Barkyn hide their quiz but keep the widget on
    // the plan page whose path starts with the same words (11 Sep 2026).
    const here = pathname.toLowerCase().replace(/\/+$/, "") || "/";
    const hidden = props.hiddenPaths.some((raw) => {
      if (typeof raw !== "string" || !raw.trim()) return false;
      const p = raw.trim().toLowerCase();
      if (p.endsWith("$")) return here === (p.slice(0, -1).replace(/\/+$/, "") || "/");
      return here.includes(p.replace(/\/+$/, "") || "/");
    });
    if (hidden) return null;
  }

  if (props.launcher?.mode === "whatsapp") {
    const wa = props.whatsapp;
    // No number = nothing to link to. Render nothing rather than a dead button.
    if (!wa?.number) return null;

    // Per-device variant: mobile (<768px, the useIsMobile breakpoint) vs desktop,
    // each field falling back to the legacy flat launcher fields, then defaults —
    // so a pre-variant row (flat radius/label only) renders exactly as before.
    const variant = (isMobile ? props.launcher.mobile : props.launcher.desktop) ?? {};
    const label = (variant.label ?? props.launcher.label ?? "").trim();
    // Default 9999 = pill. 0 is honoured (square), so only undefined falls back.
    const radius =
      typeof variant.radius === "number"
        ? variant.radius
        : typeof props.launcher.radius === "number"
          ? props.launcher.radius
          : 9999;
    // Button height; the glyph scales with it. 56 ≈ the pre-variant footprint.
    const size = typeof variant.size === "number" && variant.size > 0 ? variant.size : 56;
    const iconSize = Math.max(18, Math.round(size * 0.46));
    const corner = variant.corner === "bottom-left" ? "left" : "right";
    const offsetX = typeof variant.offset_x === "number" ? variant.offset_x : 24;
    // The vertical gap is `launcherBottom` above: the same variant offset, but
    // already clear of the page's own bottom bar when the widget asks for that.
    const openWhatsApp = () => {
      const opener = withPageContext(wa.prefill_text || "", pageContext);
      const text = opener ? `?text=${encodeURIComponent(opener)}` : "";
      window.open(`https://wa.me/${wa.number}${text}`, "_blank");
    };

    return (
      <>
      <button
        onClick={openWhatsApp}
        aria-label={label || "Chat on WhatsApp"}
        className="fixed z-[2147483647] flex items-center justify-center gap-2.5 shadow-lg transition-transform duration-200 hover:scale-105 active:scale-95"
        style={{
          bottom: `${launcherBottom}px`,
          [corner]: `${offsetX}px`,
          // WhatsApp green / white by default; editable per widget since 13 Aug 2026.
          backgroundColor: props.launcher.background_color || "#25D366",
          color: props.launcher.text_color || "#FFFFFF",
          borderRadius: `${radius}px`,
          height: `${size}px`,
          // Icon-only stays a circle/square; with a label it becomes a pill.
          width: label ? undefined : `${size}px`,
          padding: label ? `0 ${Math.round(size * 0.32)}px` : 0,
        }}
      >
        {/* Inline glyph — the home-screen tile uses a flaticon CDN URL, which
            would be a third-party dependency on every client's storefront. */}
        <svg
          viewBox="0 0 24 24"
          width={iconSize}
          height={iconSize}
          fill="currentColor"
          aria-hidden="true"
          className="shrink-0"
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
        {label && (
          <span
            className="font-semibold whitespace-nowrap pr-0.5"
            style={{ fontSize: `${Math.max(12, Math.round(size * 0.27))}px` }}
          >
            {label}
          </span>
        )}
      </button>
      {launcherCaption ? (
        <button
          type="button"
          onClick={openWhatsApp}
          style={{ ...captionStyle, bottom: launcherBottom + size + 8, [corner]: offsetX }}
        >
          {launcherCaption}
        </button>
      ) : null}
      </>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  // CENTRE ("Ask" / formerly Rylo) CHROME
  // ════════════════════════════════════════════════════════════════════════════
  if (displayMode === "center") {
    // Editable per widget via web_chat_widgets.center_config; falls back to
    // the built-in defaults when not configured.
    const configuredSuggestions = (props.centerConfig?.suggestions || []).filter(
      (s) => typeof s === "string" && s.trim()
    );
    const centerSuggestions =
      configuredSuggestions.length > 0
        ? configuredSuggestions
        : [
            `How does ${effectiveHeaderText} help my sales team close more deals?`,
            `How fast can I get ${effectiveHeaderText} running on my website?`,
            `Can I customize how the widget looks?`,
          ];
    const centerPlaceholder =
      props.centerConfig?.placeholder?.trim() ||
      `Ask ${effectiveHeaderText} anything…`;

    // Open the chat FIRST, stash the message. ChatSession mounts, loads its
    // history, then fires the message — so it never races the open/load.
    const startCenterChat = (question: string) => {
      const q = question.trim();
      if (!q) return;
      setCenterInput("");
      setPendingCenterMessage(q);
      setCurrentView("chat");
      setIsOpen(true);
      // Scroll locking handled by the body position:fixed effect.
    };

    const ModalContainer = enableAnimations ? motion.div : "div";
    const modalAnim = enableAnimations
      ? {
          initial: { opacity: 0, scale: 0.96, y: 24 },
          animate: { opacity: 1, scale: 1, y: 0 },
          exit: { opacity: 0, scale: 0.96, y: 24 },
          transition: {
            type: "spring" as const,
            stiffness: 500,
            damping: 35,
            mass: 0.8,
          },
        }
      : {};

    const modalEl = isOpen ? (
      <div className="fixed inset-x-0 bottom-28 z-[2147483646] flex justify-center px-3 pointer-events-none">
        <ModalContainer
          ref={chatContainerRef}
          className="pointer-events-auto relative w-full max-w-3xl border border-gray-200 shadow-2xl rounded-2xl flex flex-col overflow-hidden"
          style={{
            height: isMobile ? "76vh" : "70vh",
            maxHeight: isMobile ? "76vh" : "640px",
            backgroundColor: "#FFFFFF",
            // Smooth in-place growth for the enlarged state (stays centred).
            transition: `max-width 400ms ${EXPAND_EASE}, height 400ms ${EXPAND_EASE}, max-height 400ms ${EXPAND_EASE}`,
            ...(currentView === "home" ? customStylesObj : {}),
            ...(expanded && !isMobile
              ? {
                  maxWidth: "min(1120px, calc(100vw - 32px))",
                  height: "calc(100vh - 150px)",
                  maxHeight: "calc(100vh - 150px)",
                }
              : {}),
          }}
          {...modalAnim}
        >
          {panelControls}
          {renderView()}
        </ModalContainer>
      </div>
    ) : null;

    return (
      <>
        {/* Suggestion chips — only when closed (and not host-controlled) */}
        {!isOpen && !props.hideLauncher && (
          <div className="fixed bottom-[6.5rem] left-0 right-0 z-[2147483645] px-4 pointer-events-none">
            <div className="flex flex-wrap items-center justify-center gap-2 max-w-2xl mx-auto pointer-events-auto">
              {centerSuggestions.map((q, i) => (
                <button
                  key={q}
                  onClick={() => startCenterChat(q)}
                  style={
                    enableAnimations
                      ? { animation: `cw-rise 0.45s ease ${0.05 * i + 0.05}s both` }
                      : undefined
                  }
                  className="group inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white/70 backdrop-blur-md border border-black/5 text-[13px] font-medium text-gray-500 shadow-[0_2px_10px_rgba(0,0,0,0.05)] hover:text-gray-900 hover:bg-white hover:shadow-[0_6px_20px_rgba(0,0,0,0.1)] hover:-translate-y-0.5 transition-all duration-200"
                >
                  <Sparkles className="w-3 h-3 text-gray-300 group-hover:text-gray-500 transition-colors" />
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Centre modal — the SAME shared views as corner mode */}
        {enableAnimations ? (
          <AnimatePresence mode="wait">{modalEl}</AnimatePresence>
        ) : (
          modalEl
        )}

        {/* "Ask" launcher — a real typeable bar that weaves the message through
            the shared ChatSession engine (no separate engine wiring). */}
        <div className="fixed bottom-7 left-1/2 -translate-x-1/2 z-[2147483647] w-[min(92vw,30rem)]">
          {isOpen ? (
            // Open: minimal close control sitting under the modal.
            <button
              onClick={toggleChat}
              aria-label="Close chat"
              className="mx-auto flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/90 backdrop-blur border border-black/5 text-sm font-medium text-gray-500 shadow-lg hover:text-gray-900 hover:shadow-xl transition-all duration-200"
            >
              <X size={16} />
              Close
            </button>
          ) : props.hideLauncher ? null : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                startCenterChat(centerInput);
              }}
              className="relative group"
            >
              {/* soft gradient halo */}
              <div className="absolute -inset-[2px] rounded-[1.6rem] bg-[linear-gradient(90deg,#111827,#9ca3af,#111827)] opacity-25 blur-md group-focus-within:opacity-50 transition-opacity duration-300" />
              <div className="relative flex items-center gap-2.5 bg-white rounded-[1.6rem] pl-4 pr-2 py-2 shadow-[0_10px_40px_-8px_rgba(0,0,0,0.25)] ring-1 ring-black/5">
                <button
                  type="button"
                  onClick={toggleChat}
                  aria-label={`Open ${effectiveHeaderText}`}
                  className="shrink-0 rounded-full transition-transform duration-200 hover:scale-110 active:scale-95"
                >
                  <img
                    src={ORB_IMG}
                    alt=""
                    aria-hidden="true"
                    draggable={false}
                    className="w-6 h-6 object-contain select-none"
                  />
                </button>
                <input
                  value={centerInput}
                  onChange={(e) => setCenterInput(e.target.value)}
                  placeholder={centerPlaceholder}
                  aria-label={`Ask ${effectiveHeaderText}`}
                  className="flex-1 min-w-0 bg-transparent outline-none border-none text-[15px] text-gray-900 placeholder:text-gray-400"
                />
                <button
                  type="submit"
                  aria-label="Send"
                  disabled={!centerInput.trim()}
                  className="shrink-0 w-9 h-9 rounded-full bg-gray-900 text-white flex items-center justify-center transition-all duration-200 enabled:hover:bg-black enabled:hover:scale-105 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ArrowUp size={18} strokeWidth={2.5} />
                </button>
              </div>
            </form>
          )}
        </div>

        {/* keyframes for chip entrance (scoped, injected once) */}
        <style>{`@keyframes cw-rise{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}`}</style>
      </>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  // CORNER (classic bubble + side panel) CHROME
  // ════════════════════════════════════════════════════════════════════════════
  return (
    <>
      {/* ============ FRAMER MOTION: Animated Toggle Button - START ============ */}
      {!(mobileFullscreen && isOpen) && (
        <ToggleButton
          onClick={toggleChat}
          className={
            orbLauncher && !isOpen
              ? "fixed p-0 rounded-full z-[2147483647] overflow-hidden"
              : orbLauncher
                ? // Open orb launcher: the themed close disc, but at the orb's
                  // own size with the X centred, so the button neither shrinks
                  // nor shifts its centre when the chat opens (21 Sep 2026).
                  `fixed p-0 flex items-center justify-center ${
                    theme === "light"
                      ? "bg-white text-black hover:bg-gray-100 border border-gray-200"
                      : "bg-black text-white hover:bg-zinc-800"
                  } rounded-full shadow-lg z-[2147483647]`
                : `fixed p-4 ${
                    theme === "light"
                      ? "bg-white text-black hover:bg-gray-100 border border-gray-200"
                      : "bg-black text-white hover:bg-zinc-800"
                  } rounded-full shadow-lg z-[2147483647]`
          }
          style={{
            ...getButtonPositionStyle(),
            // The orb launcher keeps ONE footprint open or closed (72px or the
            // launcher size): closed it is the orb itself, open it is a disc
            // of the same size in the same place.
            ...(orbLauncher ? { width: orbSize, height: orbSize } : {}),
            ...(orbLauncher && !isOpen
              ? {
                  // The orb IS the button (launcher.icon "orb", 21 Sep 2026):
                  // no disc, no shadow, sized like the loader drew it so
                  // nothing jumps when the bundle takes over.
                  background: "transparent",
                  boxShadow: "none",
                  border: "none",
                }
              : {}),
            ...(!isOpen && !orbLauncher
              ? {
                  // The launcher's own colours win over the panel's primary
                  // colour (11 Sep 2026), so a WhatsApp-marked bubble can be
                  // WhatsApp green without turning the whole widget green.
                  backgroundColor:
                    props.launcher?.background_color || customColors.buttonBackgroundColor,
                  color: props.launcher?.text_color || customColors.buttonTextColor,
                  borderColor:
                    props.launcher?.background_color || customColors.borderColor,
                }
              : {}),
          }}
          aria-label="Toggle chat"
          {...toggleButtonProps}
        >
          {isOpen ? (
            <X size={24} />
          ) : orbLauncher ? (
            // The same shader the loader's launcher draws, on the same clock.
            <OrbCanvas poster={orbAssets.poster} />
          ) : props.launcher?.icon === "whatsapp" ? (
            // The loader draws this same mark on its own launcher; the two must
            // match or the button changes glyph the moment the bundle loads.
            <svg viewBox="0 0 24 24" width={24} height={24} fill="currentColor" aria-hidden="true">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
          ) : (
            <MessageSquare size={24} />
          )}
        </ToggleButton>
      )}
      {!isOpen && !props.hideLauncher && launcherCaption ? (
        <button
          type="button"
          onClick={toggleChat}
          style={{
            ...captionStyle,
            ...(position.includes("top")
              ? { top: launcherGapY + (orbLauncher ? orbSize : 56) + 8 }
              : { bottom: launcherBottom + (orbLauncher ? orbSize : 56) + 8 }),
            ...(position.includes("left") ? { left: launcherGapX } : { right: launcherGapX }),
          }}
        >
          {launcherCaption}
        </button>
      ) : null}
      {/* ============ FRAMER MOTION: Animated Toggle Button - END ============ */}

      {/* ============ FRAMER MOTION: AnimatePresence Wrapper - START ============ */}
      {enableAnimations ? (
        <AnimatePresence mode="wait">
          {isOpen && (
            <Container
              ref={chatContainerRef}
              className={`${getWidgetPositionClasses()} ${
                themeClasses.background
              } ${
                mobileFullscreen ? "" : `border ${themeClasses.border} shadow-xl`
              } flex flex-col overflow-hidden`}
              style={cornerPanelStyle}
              {...containerProps}
            >
              {panelControls}
              {renderView()}
            </Container>
          )}
        </AnimatePresence>
      ) : (
        <>
          {isOpen && (
            <div
              ref={chatContainerRef}
              className={`${getWidgetPositionClasses()} ${
                themeClasses.background
              } ${
                mobileFullscreen ? "" : `border ${themeClasses.border} shadow-xl`
              } flex flex-col overflow-hidden`}
              style={cornerPanelStyle}
            >
              {panelControls}
              {renderView()}
            </div>
          )}
        </>
      )}
      {/* ============ FRAMER MOTION: AnimatePresence Wrapper - END ============ */}
    </>
  );
};

export default ChatWidget;
